/**
 * F29 (Wave 2C) — admin teams API client.
 *
 * Endpoints (per `plans/29_F29_teams.md` §7 + `apps/api/src/routes/admin/teams.ts`):
 *   GET    /admin/teams                            → searchTeamsAdmin (paginated, filters)
 *   GET    /admin/teams/:teamId                    → getTeamByIdAdmin (full detail)
 *   GET    /admin/teams/:teamId/audit-log          → getTeamAuditLog (paginated)
 *   POST   /admin/teams/:teamId/suspend            → suspendTeam (teams:moderate)
 *   POST   /admin/teams/:teamId/reinstate          → reinstateTeam (teams:moderate)
 *   POST   /admin/teams/:teamId/disband            → forceDisbandTeam (teams:moderate)
 *
 * Permission gating happens server-side (super_admin + content_moderator for
 * moderate; super_admin + support + content_moderator for read). The page-level
 * <ProtectedRoute> gates which routes a given admin role can navigate to; this
 * client forwards requests and relies on the server to enforce RBAC.
 *
 * The vitest alias in `apps/admin/vitest.config.ts` rewrites every import of
 * `./api/teams` (or `../api/teams`) to `./api/teams.fixture` at test time so
 * component tests render against deterministic in-memory state.
 */
import type {
  AdminPageMeta,
  DisbandTeamByAdminInput,
  ReinstateTeamInput,
  RemoveTeamMemberInput,
  SportName,
  SuspendTeamInput,
  Team,
  TeamAuditEntry,
  TeamMemberWithUser,
  TeamStatus,
} from '@bravemove/types';
import { adminFetch, buildQuery } from '../auth/apiClient.js';

// ─── Response envelopes ────────────────────────────────────────────────────────

/** A row in the admin teams queue — `Team` enriched with captain + creator
 *  display names so the table doesn't have to round-trip per row. */
export interface AdminTeamRow extends Team {
  readonly captainDisplayName: string | null;
  readonly createdByDisplayName: string | null;
}

/** Global per-status totals for the queue KPI strip (TGS-02). */
export interface AdminTeamStatusCounts {
  readonly active: number;
  readonly suspended: number;
  readonly disbanded: number;
}

export interface AdminTeamsListResponse {
  readonly data: readonly AdminTeamRow[];
  readonly nextCursor: string | null;
  /** Present when `page` is supplied (page-numbered pagination). */
  readonly pagination?: AdminPageMeta;
  /** Global (unfiltered) per-status totals — drives the KPI strip without a
   *  second fetch. Optional for forward-compat with older server builds. */
  readonly statusCounts?: AdminTeamStatusCounts;
}

export interface AdminTeamDetailResponse {
  readonly team: Team;
  readonly captain: TeamMemberWithUser | null;
  readonly members: readonly TeamMemberWithUser[];
  readonly recentAuditEntries: readonly TeamAuditEntry[];
  readonly suspendedByAdminDisplayName: string | null;
}

export interface AdminTeamAuditLogResponse {
  readonly data: readonly TeamAuditEntry[];
  readonly nextCursor: string | null;
}

// ─── Query option types ────────────────────────────────────────────────────────

export interface AdminTeamsSearchOpts {
  readonly q?: string;
  readonly status?: TeamStatus | 'all';
  readonly sportName?: SportName;
  readonly cazaCode?: string;
  readonly cursor?: string;
  readonly limit?: number;
  /** 1-based page index (page-numbered pagination). */
  readonly page?: number;
  /** Page size (default 25 server-side, max 100). */
  readonly perPage?: number;
}

export interface AdminTeamAuditLogOpts {
  /** ISO-8601 `createdAt` of the previous page's last row (the backend's
   *  keyset cursor — `GET .../audit-log?beforeCreatedAt=`). The route's
   *  `.strict()` query schema accepts ONLY `limit` + `beforeCreatedAt`;
   *  the old `cursor` name 422'd (TGS-04). */
  readonly beforeCreatedAt?: string;
  readonly limit?: number;
}

// ─── API surface ───────────────────────────────────────────────────────────────

export const adminTeamsApi = {
  /** GET /admin/teams — paginated search (filters: status / sport / caza / q). */
  search: (opts?: AdminTeamsSearchOpts): Promise<AdminTeamsListResponse> =>
    adminFetch<AdminTeamsListResponse>(
      `/admin/teams${buildQuery({
        q: opts?.q,
        status: opts?.status === 'all' ? undefined : opts?.status,
        sportName: opts?.sportName,
        cazaCode: opts?.cazaCode,
        cursor: opts?.cursor,
        limit: opts?.limit,
        page: opts?.page,
        perPage: opts?.perPage,
      })}`,
    ),

  /** GET /admin/teams/:teamId — full team detail (roster + recent audit). */
  detail: async (teamId: string): Promise<AdminTeamDetailResponse> => {
    // The server wraps the detail projection in a `{ data }` envelope (see
    // `apps/api/src/routes/admin/teams.ts` GET /admin/teams/:teamId →
    // `reply.send({ data: view })`), matching the other admin detail clients
    // (commission / adminBookings / emailSuppressions all unwrap `.data`).
    // Without this unwrap the page receives `{ data: {...} }`, reads
    // `detail.team.status` on the envelope → "Cannot read properties of
    // undefined (reading 'status')" and the whole page blanks.
    const body = await adminFetch<{ data: AdminTeamDetailResponse }>(`/admin/teams/${teamId}`);
    return body.data;
  },

  /** GET /admin/teams/:teamId/audit-log — paginated audit entries (keyset
   *  pagination via `beforeCreatedAt`; pass the previous response's
   *  `nextCursor` as `beforeCreatedAt` for the next page). */
  auditLog: (teamId: string, opts?: AdminTeamAuditLogOpts): Promise<AdminTeamAuditLogResponse> =>
    adminFetch<AdminTeamAuditLogResponse>(
      `/admin/teams/${teamId}/audit-log${buildQuery({
        beforeCreatedAt: opts?.beforeCreatedAt,
        limit: opts?.limit,
      })}`,
    ),

  /** POST /admin/teams/:teamId/suspend — reversible suspension. */
  suspend: (teamId: string, body: SuspendTeamInput): Promise<{ data: unknown }> =>
    adminFetch(`/admin/teams/${teamId}/suspend`, {
      method: 'POST',
      body: body as Record<string, unknown>,
    }),

  /** POST /admin/teams/:teamId/reinstate — flip back to active. */
  reinstate: (teamId: string, body: ReinstateTeamInput): Promise<{ data: unknown }> =>
    adminFetch(`/admin/teams/${teamId}/reinstate`, {
      method: 'POST',
      body: body as Record<string, unknown>,
    }),

  /** POST /admin/teams/:teamId/disband — terminal force-disband. */
  forceDisband: (teamId: string, body: DisbandTeamByAdminInput): Promise<{ data: unknown }> =>
    adminFetch(`/admin/teams/${teamId}/disband`, {
      method: 'POST',
      body: body as Record<string, unknown>,
    }),

  /** POST /admin/teams/:teamId/members/:userId/remove — admin removes one member. */
  removeMember: (
    teamId: string,
    userId: string,
    body: RemoveTeamMemberInput,
  ): Promise<{ data: unknown }> =>
    adminFetch(`/admin/teams/${teamId}/members/${userId}/remove`, {
      method: 'POST',
      body: body as Record<string, unknown>,
    }),
};
